import { useEffect, useState } from 'react';

import type { CreatorDiscoveryInstagramAccount } from '@frontend/app/hooks';

const SELECTED_INSTAGRAM_ACCOUNT_KEY = 'aspire.creatorDiscovery.selectedInstagramAccountId';

interface UseSelectedInstagramAccountProps {
  instagramAccounts: CreatorDiscoveryInstagramAccount[];
}

export const useSelectedInstagramAccount = ({ instagramAccounts }: UseSelectedInstagramAccountProps) => {
  const [selectedInstagramAccount, setSelectedInstagramAccount] = useState<
    CreatorDiscoveryInstagramAccount | undefined
  >(undefined);

  // Set default to first Instagram account when data loads, or use saved from localStorage
  useEffect(() => {
    if (instagramAccounts.length > 0 && !selectedInstagramAccount) {
      // Try to get saved ID from localStorage
      try {
        const savedId = localStorage.getItem(SELECTED_INSTAGRAM_ACCOUNT_KEY);

        if (savedId) {
          // Find the account with the saved ID
          const savedAccount = instagramAccounts.find((account) => account.igUserId === savedId);
          if (savedAccount) {
            setSelectedInstagramAccount(savedAccount);
            return;
          }
        }
      } catch (error) {
        console.warn('Failed to read from localStorage:', error);
      }

      // Fall back to first account if no saved ID or account not found
      setSelectedInstagramAccount(instagramAccounts[0]);
    }
  }, [instagramAccounts, selectedInstagramAccount]);

  // Save selected Instagram account ID to localStorage when it changes
  useEffect(() => {
    if (selectedInstagramAccount) {
      try {
        localStorage.setItem(SELECTED_INSTAGRAM_ACCOUNT_KEY, selectedInstagramAccount.igUserId);
      } catch (error) {
        console.warn('Failed to save to localStorage:', error);
      }
    }
  }, [selectedInstagramAccount]);

  return {
    selectedInstagramAccount,
    setSelectedInstagramAccount,
  };
};
